// Vector tiles for a named set of wells, via `/queries/wells/mvt`. Unlike the shared
// `wells_v2` source this path carries no minzoom, so scoped wells draw at any zoom.
import type { Expression } from 'mapbox-gl';
import { chunk } from 'lodash-es';

// One request per 500 UWIs: the set goes in the query string, so it can't be unbounded.
// The same size Type Curve's and pre-drill's own scoped layers use.
export const UWI_CHUNK_SIZE = 500;

/** Which geometry to draw: the deviation survey, or the surface→bottom-hole stick. */
export type UwiTileGeometry = 'survey' | 'stick';

// Tile URL templates for these UWIs, one per chunk. Chunking is deterministic, so the
// survey and stick lists line up index for index.
export const uwiTileUrls = (
  uwis: string[],
  geometry: UwiTileGeometry = 'survey'
): string[] => {
  if (uwis.length === 0) {
    return [];
  }
  const baseUrl = `${window.location.origin}/queries/wells/mvt`;
  return chunk(uwis, UWI_CHUNK_SIZE).map((group) => {
    const params = new URLSearchParams({ uwis: group.join(',') });
    return `${baseUrl}/${geometry}/{z}/{x}/{y}.mvt?${params.toString()}`;
  });
};

// A mapbox `match` on uwi, grouped by colour so the expression carries one case per
// distinct colour rather than one per well.
export const buildUwiColorExpr = (
  uwiColorMap: Record<string, string>,
  fallback: string
): string | Expression => {
  const entries = Object.entries(uwiColorMap);
  if (entries.length === 0) {
    return fallback;
  }

  const colorGroups = new Map<string, string[]>();
  entries.forEach(([uwi, color]) => {
    const group = colorGroups.get(color);
    if (group) {
      group.push(uwi);
    } else {
      colorGroups.set(color, [uwi]);
    }
  });

  const flat: (string | string[])[] = [];
  colorGroups.forEach((uwis, color) => {
    flat.push(uwis.length === 1 ? uwis[0] : uwis, color);
  });
  return ['match', ['get', 'uwi'], ...flat, fallback] as Expression;
};
